import { ReserveAuctionOrder, SignedSaleOrder } from "@/api/orders/types";
import { getEIP712SignatureParams } from "@/eip712";
import {
  RESERVE_AUCTION_INFO_PARAMS,
  RESERVE_AUCTION_PARAMS,
} from "@/eip712/constants";
import { buildObject } from "@/shared/helpers";
import { useCallback } from "react";
import { useExchangeContractProxy } from "../useExchangeContract";

function getReserveOrdersEIP712Params(
  reserveAuction: SignedSaleOrder,
  highestBid: SignedSaleOrder,
  approvalSignature: string,
) {
  const auctionEIP712Params = getEIP712SignatureParams(
    reserveAuction.signature!,
  );

  const bidEIP712Params = getEIP712SignatureParams(highestBid.signature!);

  const approvalEIP712Params = getEIP712SignatureParams(approvalSignature);

  return {
    auctionEIP712Params,
    bidEIP712Params,
    approvalEIP712Params,
  };
}

const renameMap = Object.freeze({
  makerFee: "makerFeePercentage",
  takerFee: "takerFeePercentage",
  royaltiesFee: "royaltiesPercentage",
  collectiveFee: "collectiveFeePercentage",
});

export default function useSettleReserveAuction(
  auction: ReserveAuctionOrder,
  bid: ReserveAuctionOrder,
) {
  const exchangeContract = useExchangeContractProxy();

  return useCallback(
    (approvalSignature: string) => {
      const { auctionEIP712Params, bidEIP712Params, approvalEIP712Params } =
        getReserveOrdersEIP712Params(auction, bid, approvalSignature);

      const eip712AuctionObject = buildObject(
        {
          ...auction,
          ...auctionEIP712Params,
          signer: auction.seller,
        },
        {},
        [...RESERVE_AUCTION_PARAMS],
      );

      const eip712BidObject = buildObject(
        {
          ...bid,
          ...bidEIP712Params,
          signer: auction.buyer,
          price: bid.totalPrice,
        },
        {},
        [...RESERVE_AUCTION_PARAMS],
      );

      const auctionInfoObject = buildObject(auction, renameMap, [
        ...RESERVE_AUCTION_INFO_PARAMS,
      ]);

      const { v, r, s } = approvalEIP712Params;

      return exchangeContract!.finalizeReserveAuction(
        v,
        r,
        s,
        eip712AuctionObject,
        eip712BidObject,
        auctionInfoObject,
      );
    },
    [auction, bid, exchangeContract],
  );
}
